/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-09-20 20:16:59
 **/
/* eslint-disable no-empty */
/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-unused-vars */

import * as mediasoup from "mediasoup-client";
import { EventEmitter } from "eventemitter3";
import DetectRTC from "detectrtc";

import { EventHandler } from "@rfice/eventhandler";
//*
import SoundMeter from "./sound_meter";
import XmsNetwork from "./xms_network";
import XmsUtils from "./xms_utils";
/*/
import { SoundMeter, XmsNetwork, XmsUtils } from "@rfice/xms";
//*/

import { Properties } from "@/frameworks/libraries/properties";
import { Environments, Properties as LibProperties } from "@rsupport/libraries";
import { Configure, Ticker } from "@/frameworks/libraries/utilities";
import { DeviceManager } from "@rsupport/devicemanager";
import DummyStream from "./dummystream";
import RNNAudioHandler from "@/frameworks/libraries/media/rnnaudiohandler";
import { LocalData } from "@/frameworks/libraries";
import { boolean } from "zod";
import UnityApiController from "../../unity/unityApiController";
const LOGTAG = "#xms_client#";

// const mediasoup = require('mediasoup-client');
// // const XmsNetwork = require('./XmsNetwork');
// const EventEmitter = require('events').EventEmitter;

// https://mediasoup.org/documentation/v3/mediasoup-client/api/#ProducerOptions
// https://mediasoup.org/documentation/v3/mediasoup-client/api/#transport-produce
// let params = {
// 	// mediasoup params
// 	encodings: [
// 	  {
// 		rid: 'r0',
// 		maxBitrate: 100000,
// 		scalabilityMode: 'S1T3',
// 	  },
// 	  {
// 		rid: 'r1',
// 		maxBitrate: 300000,
// 		scalabilityMode: 'S1T3',
// 	  },
// 	  {
// 		rid: 'r2',
// 		maxBitrate: 900000,
// 		scalabilityMode: 'S1T3',
// 	  },
// 	],
// 	// https://mediasoup.org/documentation/v3/mediasoup-client/api/#ProducerCodecOptions
// 	codecOptions: {
// 	  videoGoogleStartBitrate: 1000
// 	}
// };

// let audioParams = { appData: { producerType: 'camMic'} };
// let videoParams = { appData: { producerType: 'camMic'}, ...params };

const params = {
	// mediasoup params
	encodings: [
		{
			rid: "r0",
			maxBitrate: 100000,
			scalabilityMode: "S1T3"
		},
		{
			rid: "r1",
			maxBitrate: 300000,
			scalabilityMode: "S1T3"
		},
		{
			rid: "r2",
			maxBitrate: 900000,
			scalabilityMode: "S1T3"
		}
	],
	// https://mediasoup.org/documentation/v3/mediasoup-client/api/#ProducerCodecOptions
	codecOptions: {
		videoGoogleStartBitrate: 1000
	}
};

const maxNumOfParticipantsToDisplay = 3;

const AudioContext = window.AudioContext || window.webkitAudioContext;

export default class XmsClient extends EventEmitter {
	_dummyStream = new DummyStream();

	// constructor({configure, config, xmsNetwork, audioParams, videoParams, actorId})
	constructor(options) {
		super();

		options && this.initialize(options);
	}

	async initialize({ configure, onLocalStream, actorId, handler, roomId, userId }) {
		this.configure = configure;
		this.roomId = roomId;
		this.userId = userId;

		this.xmsNetwork = new XmsNetwork("xrm");
		await this.xmsNetwork.init(roomId, userId);
		this.xmsNetwork.on("message", (msgType, payload) => this.processMessage(msgType, payload));

		this.peers = new Map();
		this.mediasoupDevice = null;
		this.sendTransport = null;
		this.recvTransport = null;
		this.audioProducer = null;
		this.videoProducer = null;
		this.screenAudioProducer = null;
		this.screenVideoProducer = null;
		this.screenStream = null;
		// this.audioParams = audioParams;
		// this.videoParams = videoParams;
		this.audioParams = null;
		this.videoParams = null;
		this.actorId = actorId;
		this.onLocalStream = onLocalStream;
		this.audioVideoStream = null;

		// talkingGroup은 userName으로 구성된 Array
		// talkingGroupWithUserId는 talkingGroup에서 현재 peers에 있는 참여자들에 대한 userId들로 구성된 Array
		this.talkingGroup = [];
		this.talkingGroupWithUserId = [];

		this.participants = new Map();

		this._status = "creating";

		this._soundMeter = null;
		this._rnnaudio = null;

		this.heartbeatCount = 0;
		this.heartbeatTimer = null;

		this.dominantSpeakers = [];
		this.participantsToDisplay = [];

		this.handler = handler;

		this.enableDominantSpeaker = false;
		this.enableLoudestSpeakers = false;

		this.numOfLoudestSpeakers = 5;
	}

	get printable() {
		return true; //Configure.useLogLevel > 5;
	}

	get status() {
		return this._status;
	}

	set status(value) {
		this._status = value;
		this.printable && console.debug(LOGTAG, `current status`, this._status);
		this.handler?.("status", this.roomId, { status: this._status });
	}

	get soundMeter() {
		if (this._soundMeter === null) {
			this._soundMeter = new SoundMeter("XmsClient", new AudioContext());
		}
		return this._soundMeter;
	}

	get RNNAudio() {
		if (this._rnnaudio === null) {
			const { use_noise_cancellation } = LibProperties.instance.get("device_options");
			this._rnnaudio = new RNNAudioHandler(false, use_noise_cancellation);
		}
		return this._rnnaudio;
	}

	getPeers() {
		return this.peers;
	}

	get local_stream() {
		return this.audioVideoStream;
	}

	getParticipantsInJson() {
		const currentParticipants = [];

		this.participants.forEach((value) => {
			currentParticipants.push(value);
		});

		const message = JSON.stringify(currentParticipants);
		return `{"actors":${message}}`;
		// this.printable && console.debug(LOGTAG, `participants`, this.participants);
		// this.printable && console.debug(LOGTAG, `participants in JSON`, jsonMsg);
	}

	getParticipantsToDisplayInJson() {
		const message = JSON.stringify(this.participantsToDisplay);
		return `{"actors":${message}}`;
		// this.printable && console.debug("participantsToDisplay: ", this.participantsToDisplay);
		// this.printable && console.debug("participantsToDisplay in JSON: ", jsonMsg);
	}

	// startDevicesListener()
	// {
	// 	navigator.mediaDevices.addEventListener('devicechange', async () =>
	// 	{
	// 		this.printable & console.debug('_startDevicesListener() | navigator.mediaDevices.ondevicechange');

	// 		await this._updateAudioDevices();
	// 		await this._updateWebcams();
	// 		await this._updateAudioOutputDevices();

	// 		store.dispatch(requestActions.notify(
	// 			{
	// 				text : intl.formatMessage({
	// 					id             : 'devices.devicesChanged',
	// 					defaultMessage : 'Your devices changed, configure your devices in the settings dialog'
	// 				})
	// 			}));
	// 	});
	// }

	// async startAudioVideo()
	// {
	//     try {
	//         if (mediasoup)
	//         // const stream = await navigator.mediaDevices.getUserMedia({
	//         //     audio: true,
	//         //     video: {
	//         //         width: {
	//         //             min: 320,
	//         //             max: 1280,
	//         //         },
	//         //         height: {
	//         //             min: 180,
	//         //             max: 720,
	//         //         }
	//         //     }
	//         // });
	//     }
	//     catch (err) {

	//     }
	// }

	// enableLoudestSpeakers가 true이면 enableDominantSpeaker는 자동으로 true임.
	async join(roomId, userId, enableDominantSpeaker = false, enableLoudestSpeakers = false) {
		this.status = "joining";

		this.enableLoudestSpeakers = enableLoudestSpeakers;
		this.enableDominantSpeaker = enableDominantSpeaker;

		this.printable && console.debug(LOGTAG, `join(${roomId}, ${userId})`);
		try {
			await this.sendJoin();

			// loudestSpeakers용으로 생성된 consumer들을 위해서 필요함.
			if (enableLoudestSpeakers) {
				this.printable && console.debug(LOGTAG, `numOfLoudestSpeakers `, this.numOfLoudestSpeakers);
				for (let i = 0; i < this.numOfLoudestSpeakers; i++) {
					// this.numOfLoudestSpeakers from joinResponse
					const xmsId = `loudestSpeakers_${i}`;
					const newPeer = {
						userId: xmsId,
						actorId: String(Number(this.actorId) + 1000),
						consumers: new Map(),
						isPinned: false
					};

					this.peers.set(xmsId, newPeer);
				}
			}

			const serverRtpCapabilities = await this.sendGetServerCapabilities();
			this.printable && console.debug(LOGTAG, `serverRtpCapabilities`, serverRtpCapabilities);

			this.mediasoupDevice = new mediasoup.Device();
			await this.mediasoupDevice.load({ routerRtpCapabilities: serverRtpCapabilities });

			if (this.configure.mediaDevice === "CAMERA") {
				this.printable && console.debug(LOGTAG, `join with camera`);

				let enableAudio = false;
				if (this.mediasoupDevice.canProduce("audio") && DetectRTC.hasMicrophone) {
					this.printable && console.debug(LOGTAG, `audio device is available.`);
					enableAudio = true;
				}

				let enableVideo = false;
				if (this.mediasoupDevice.canProduce("video") && DetectRTC.hasWebcam) {
					this.printable && console.debug(LOGTAG, `video device is available.`);
					enableVideo = true;
				}

				const { width, height } = XmsUtils.getVideoResolution(this.configure.resolution);
				this.printable && console.debug(LOGTAG, `video max width = ${width}, height = ${height}`);

				let constraints = {};
				if (enableAudio) {
					constraints = DeviceManager.instance.get_media_constraints({
						...constraints,
						audio: {
							...(Configure.useRMAudioOption
								? {
										optional: [
											{ googEchoCancellation: true },
											{ googAutoGainControl: true },
											{ googNoiseSuppression: true },
											{ googHighpassFilter: true },
											{ googAudioMirroring: true },
											// { googNoiseSuppression2: true },
											{ googEchoCancellation2: true }
											// { googAutoGainControl2: true },
											// { googDucking: false },
											// { chromeRenderToAssociatedSink: true }
										]
									}
								: { autoGainControl: true, echoCancellation: true, noiseSuppression: true })
						}
					});
				}

				try {
					this.printable && console.debug(LOGTAG, `join constraints`, constraints);
					EventHandler.fire("$Device::Permission$");
					this.audioVideoStream = await navigator.mediaDevices.getUserMedia(constraints);
					this.audioVideoStream.addTrack(this._dummyStream.create(width, height).getVideoTracks()[0]);
				} catch (error) {
					console.warn(LOGTAG, `error in getUserMedia(audio)`, error);
					enableVideo = false;
					enableAudio = false;
				}

				if (enableAudio || enableVideo) {
					this.printable &&
						console.debug(
							LOGTAG,
							"audioVideoStream",
							this.audioVideoStream,
							this.audioVideoStream.getTracks()
						);
					``;
				}

				if (enableAudio) {
					const { use_noise_cancellation } = LibProperties.instance.get("device_options");
					if (use_noise_cancellation) {
						this.audioVideoStream.getTracks().forEach((track) => {
							console.debug(LOGTAG, "Before Track", track.kind, track.label, track.enabled);
						});
						if (!Environments.env("UNITY_BUILD", false)) {
							await this.RNNAudio.Run(this.audioVideoStream);
							this.audioVideoStream.getTracks().forEach((track) => {
								console.debug(LOGTAG, "After Track", track.kind, track.label, track.enabled);
							});
							this.RNNAudio.Resume();
						}
					}

					this.audioParams = { appData: { producerType: "camMic" } };
					this.audioParams = { track: this.audioVideoStream.getAudioTracks()[0], ...this.audioParams };
					this.printable && console.debug(LOGTAG, `audio params`, this.audioParams);
				}

				if (enableVideo) {
					this.videoParams = { appData: { producerType: "camMic" }, ...params };
					this.videoParams = { track: this.audioVideoStream.getVideoTracks()[0], ...this.videoParams };
					this.printable && console.debug(LOGTAG, `video params`, this.videoParams);
				}

				if (enableAudio) {
					await this.soundMeter.connectToSource(this.audioVideoStream).catch((error) => {
						this.printable && console.warn(LOGTAG, `soundMeter error`, error);
					});
					this.printable && console.debug(LOGTAG, "Successfully connected to the source.");
				}

				this.handler?.("localStream", this.roomId, {
					userID: userId,
					stream: this.audioVideoStream,
					soundMeter: this.soundMeter
				});
				this.onLocalStream?.(this.audioVideoStream, userId, this.soundMeter);
			} else if (this.configure.mediaDevice === "MIC-ONLY") {
				this.printable && console.debug(LOGTAG, `join with mic-only`);

				let enableAudio = false;
				if (this.mediasoupDevice.canProduce("audio")) {
					this.printable && console.debug(LOGTAG, `audio device is available.`);
					enableAudio = true;
				}

				let constraints = {};
				if (enableAudio) {
					constraints = DeviceManager.instance.get_media_constraints({
						...constraints,
						audio: {
							...(Configure.useRMAudioOption
								? {
										optional: [
											{ googEchoCancellation: true },
											{ googAutoGainControl: true },
											{ googNoiseSuppression: true },
											{ googHighpassFilter: true },
											{ googAudioMirroring: true },
											// { googNoiseSuppression2: true },
											{ googEchoCancellation2: true }
											// { googAutoGainControl2: true },
											// { googDucking: false },
											// { chromeRenderToAssociatedSink: true }
										]
									}
								: { autoGainControl: true, echoCancellation: true, noiseSuppression: true })
						}
					});
				}

				try {
					this.printable && console.debug(LOGTAG, `join constraints`, constraints);
					EventHandler.fire("$Device::Permission$");
					this.audioVideoStream = await navigator.mediaDevices.getUserMedia(constraints);
				} catch (error) {
					console.warn(LOGTAG, `error in getUserMedia(audio)`, error);
					enableAudio = false;
				}

				if (enableAudio) {
					this.printable &&
						console.debug(
							LOGTAG,
							"audioVideoStream",
							this.audioVideoStream,
							this.audioVideoStream.getTracks()
						);

					const { use_noise_cancellation } = LibProperties.instance.get("device_options");
					if (use_noise_cancellation) {
						this.audioVideoStream.getTracks().forEach((track) => {
							console.debug(LOGTAG, "Before Track", track.kind, track.label, track.enabled);
						});
						if (!Environments.env("UNITY_BUILD", false)) {
							await this.RNNAudio.Run(this.audioVideoStream);
							this.audioVideoStream.getTracks().forEach((track) => {
								console.debug(LOGTAG, "After Track", track.kind, track.label, track.enabled);
							});
							this.RNNAudio.Resume();
						}
					}

					this.audioParams = { appData: { producerType: "camMic" } };
					this.audioParams = { track: this.audioVideoStream.getAudioTracks()[0], ...this.audioParams };
					this.printable && console.debug(LOGTAG, `audio params`, this.audioParams);

					this.soundMeter
						.connectToSource(this.audioVideoStream)
						.then(() => {
							this.printable && console.debug(LOGTAG, "Successfully connected to the source.");
						})
						.catch((error) => {
							this.printable && console.warn(LOGTAG, `soundMeter error`, error);
						});
				}

				this.handler?.("localStream", this.roomId, {
					userID: userId,
					stream: this.audioVideoStream,
					soundMeter: this.soundMeter
				});
				this.onLocalStream?.(this.audioVideoStream, userId, this.soundMeter);
			} else if (this.configure.mediaDevice === "SCREEN") {
				// deprecated
				this.printable && console.debug(LOGTAG, `join with screen sharing`);

				let stream = null;
				try {
					stream = await navigator.mediaDevices.getDisplayMedia({
						audio: true
						// video: {
						//     width: {
						//         min: 320,
						//         max: 1920,
						//     },
						//     height: {
						//         min: 180,
						//         max: 1080,
						//     }
						// }
					});
				} catch {
					EventHandler.fire("$Unity::Event$", "cancelScreenShare");
				}

				// const stream = await navigator.mediaDevices.getDisplayMedia({audio: false});

				const audioTrack = stream?.getAudioTracks()[0];
				const videoTrack = stream?.getVideoTracks()[0];

				if (audioTrack) {
					this.audioParams = { track: audioTrack, appData: { producerType: "camMic" } };
					this.printable && console.debug(LOGTAG, `audio params`, this.audioParams);
				}

				if (videoTrack) {
					this.videoParams = { track: videoTrack, appData: { producerType: "camMic" }, ...params };
					this.printable && console.debug(LOGTAG, `video params`, this.videoParams);
				}

				this.handler?.("localStream", this.roomId, { userID: userId, stream, soundMeter: null });
				this.onLocalStream?.(stream, userId, null);
			} else {
				// if (this.configure.mediaDevice === "NONE")
				this.audioParams = null;
				this.videoParams = null;

				this.handler?.("localStream", this.roomId, { userID: userId, stream: null, soundMeter: null });
				this.onLocalStream?.(null, userId, null);
			}

			if (this.configure.mediaDevice !== "NONE") {
				// produce인 경우
				const serverSendTransportParams = await this.sendCreateWebRtcTransport({ isProducer: true });

				this.sendTransport = this.createTransport(serverSendTransportParams);
				// this.printable && console.debug(LOGTAG, ``, sendTransport);
			}

			// consume인 경우
			const serverRecvTransportParams = await this.sendCreateWebRtcTransport({ isProducer: false });

			this.recvTransport = this.createTransport(serverRecvTransportParams);

			// TODO: 카메라/마이크 없는 경우
			if (this.audioParams) {
				this.audioProducer = await this.produceStream(this.audioParams);
			}

			if (this.videoParams) {
				this.videoProducer = await this.produceStream(this.videoParams);
			}

			this.dataProducer = await this.produceDataStream();

			this.sendJoinComplete();

			UnityApiController.instance.setActorId(this.actorId);

			this.heartbeatTimer = new Ticker(2000, "worker");
			this.heartbeatTimer.start(() => {
				this.heartbeatCount++;
				const text = this.heartbeatCount.toString();
				try {
					if (this.dataProducer.closed) {
						clearInterval(this.heartbeatTimer);
						this.heartbeatTimer = null;
					} else {
						this.dataProducer.send(text);
					}
				} catch {}
				// this.dataProducer.send('test message');
			});

			if (this.status === "leave") {
				this.leave();
				return;
			}

			this.status = "joined";
		} catch (error) {
			console.warn(LOGTAG, `error in join()`, error);
			this.status = "error";
			this.leave();
		}
	}

	leave() {
		this.printable && console.debug(LOGTAG, `leave() in xms_client`);

		if (this.status === "joining") {
			this.printable && console.debug(LOGTAG, `leave() in joining status!!`);
			// throw new Error('not joined user');
			this.status = "leave";
			return;
		}

		if (this.status === "error") {
			console.warn(LOGTAG, `leave() due to error!`);
		}

		this.status = "leave";

		try {
			if (this.heartbeatTimer) {
				this.heartbeatTimer.stop();
				this.heartbeatTimer = null;
			}

			this.soundMeter?.stop();
			if (!Environments.env("UNITY_BUILD", false)) {
				this.RNNAudio?.Stop().then(() => {});
			}

			this.sendLeave();

			for (const peer of this.peers.values()) {
				if (peer.soundMeter) {
					peer.soundMeter.stop();
				}

				for (const consumer of peer.consumers.values()) {
					this.removeConsumer(consumer);
					peer.consumers.delete(consumer.id);
				}
			}
			this.peers.clear();

			if (this.sendTransport) {
				this.sendTransport.close();
				this.sendTransport = null;
			}

			if (this.recvTransport) {
				this.recvTransport.close();
				this.recvTransport = null;
			}

			this.mediasoupDevice = null;
			this.audioProducer = null;
			this.videoProducer = null;
			this.screenAudioProducer = null;
			this.screenVideoProducer = null;

			this.audioVideoStream = null;
			this.screenStream = null;

			this.audioParams = null;
			this.videoParams = null;

			this.dominantSpeakers = [];
			this.participantsToDisplay = [];
			// this.sendLeave();

			if (this.xmsNetwork) {
				this.xmsNetwork.close();
				this.xmsNetwork = null;
			}

			this._dummyStream.destroy();
			this._dummyStream = null;
		} catch (error) {
			console.warn(LOGTAG, `error in leave()`, error);
		}
	}

	createTalkingGroupWithUserId(talkingGroupWithUserName) {
		const talkingGroup = talkingGroupWithUserName.map((userName) => {
			for (let peer of this.peers.values()) {
				if (peer.actorId === userName) {
					return peer.userId;
				}
			}
			return null;
		});

		return talkingGroup.filter((userId) => userId !== null);
	}

	// NOTE: 다른 참여자가 join이 완료되어 NotifyNewPeer 메시지를 수신하여야 peers에 추가됨.
	// peers에 추가되기 전에 changeTalkingGroup이 호출되면 문제가 됨
	// => talkingGroup의 참여자가 peers에 없는 경우, notifyNewPeer로 새로운 참여자가 생길때 마다 talkingGroup과
	//    비교하여 talkingGrouop에 있는 참여자인 경우 changeTalkingGroup 메시지를 보낸다.
	// talkingGroup은 join시에 사용한 userName(string)의 Array
	changeTalkingGroup(talkingGroup) {
		this.printable && console.debug(LOGTAG, `changeTalkingGroup`, talkingGroup);
		this.talkingGroup = talkingGroup;

		this.talkingGroupWithUserId = this.createTalkingGroupWithUserId(talkingGroup);

		this.printable && console.debug(LOGTAG, `converted talkingGroup`, this.talkingGroupWithUserId);

		// notifyNewPeer 메시지를 받을때, this.talkingGroup에 해당 참여자가 있는지 검사한 후, 있으면 changeTalkingGroup 메시지 전송

		this.sendChangeTalkingGroup(this.talkingGroupWithUserId);
	}

	async processMessage(msgType, payload) {
		if (!msgType || !payload) {
			console.warn(LOGTAG, `illegal message format: messageType or messagePayload is undefined`);
			return;
		}

		// this.printable & console.debug("#xms_network:in_client#", "processMessage", msgType, payload);
		switch (msgType) {
			case "dominantSpeakers": {
				this.printable && console.debug(LOGTAG, `dominantSpeakers`, payload);

				if (this.status !== "joined") {
					this.printable &&
						console.debug(
							LOGTAG,
							`The current status(${this.status}) is not joined state. Drop this dominantSpeakers message!`
						);
					break;
				}

				const updatedDominantSpeakers = [...this.dominantSpeakers];
				const dominantSpeakersHistory = payload.speakers;
				const localIndex = dominantSpeakersHistory.indexOf(this.userId);
				if (localIndex !== -1) {
					dominantSpeakersHistory.splice(localIndex, 1);
				}

				let newDominantSpeakers;
				if (dominantSpeakersHistory.length <= maxNumOfParticipantsToDisplay) {
					newDominantSpeakers = [...dominantSpeakersHistory];
				} else {
					newDominantSpeakers = dominantSpeakersHistory.slice(0, maxNumOfParticipantsToDisplay);
				}

				if (updatedDominantSpeakers.length <= newDominantSpeakers.length) {
					newDominantSpeakers.forEach((speaker) => {
						if (!updatedDominantSpeakers.includes(speaker)) {
							const unusedValues = updatedDominantSpeakers.filter(
								(o) => !newDominantSpeakers.includes(o)
							);
							if (unusedValues.length === 0) {
								updatedDominantSpeakers.push(speaker);
								this.checkAndSendGetConsumer(speaker);
							} else {
								const unusedValueIndex = updatedDominantSpeakers.indexOf(unusedValues[0]);
								updatedDominantSpeakers[unusedValueIndex] = speaker;
								this.checkAndSendRemoveConsumer(unusedValues[0]);
								this.checkAndSendGetConsumer(speaker);
							}
						}
					});
				} else {
					newDominantSpeakers.forEach((speaker) => {
						if (!updatedDominantSpeakers.includes(speaker)) {
							const unusedValues = updatedDominantSpeakers.filter(
								(o) => !newDominantSpeakers.includes(o)
							);
							if (unusedValues.length > 0) {
								const unusedValueIndex = updatedDominantSpeakers.indexOf(unusedValues[0]);
								updatedDominantSpeakers[unusedValueIndex] = speaker;
								this.checkAndSendRemoveConsumer(unusedValues[0]);
								this.checkAndSendGetConsumer(speaker);
							}
						}
					});

					const unusedValues = updatedDominantSpeakers.filter((o) => !newDominantSpeakers.includes(o));
					unusedValues.forEach((speaker) => {
						const unusedValueIndex = updatedDominantSpeakers.indexOf(speaker);
						updatedDominantSpeakers.splice(unusedValueIndex, 1);
						this.checkAndSendRemoveConsumer(speaker);
					});
				}

				this.dominantSpeakers = [...updatedDominantSpeakers];

				this.printable && console.debug(LOGTAG, `displayOrders`, this.dominantSpeakers);

				this.participantsToDisplay = [];

				this.dominantSpeakers.forEach((speaker) => {
					const participant = this.participants.get(speaker);
					if (participant) {
						this.participantsToDisplay.push(participant);
					}
				});

				// temporarily for testing
				// const dominantActorIds = [];
				// this.dominantSpeakers.forEach((speaker) => {
				// 	const peer = this.peers.get(speaker);
				// 	if (peer)   {
				// 		dominantActorIds.push(peer.actorId);
				// 	}
				// });
				// this.printable && console.debug("displayOrders(actorId): ", dominantActorIds);
				break;
			}

			case "joinResponse": {
				this.emit("joinResponse", payload.result, payload.numOfLoudestSpeakers);
				break;
			}

			case "getServerCapabilitiesResponse": {
				this.emit("getServerCapabilitiesResponse", payload.rtpCapabilities);
				break;
			}

			case "createWebRtcTransportResponse": {
				this.emit("createWebRtcTransportResponse", payload.transportParams);
				break;
			}

			case "connectWebRtcTransportResponse": {
				this.printable && console.debug(LOGTAG, `connectWebRtcTransportResponse`, payload);
				if (this.sendTransport && payload.transportId === this.sendTransport.id) {
					this.sendTransport.emit("connectWebRtcTransportResponse");
				} else if (this.recvTransport && payload.transportId === this.recvTransport.id) {
					this.recvTransport.emit("connectWebRtcTransportResponse");
				} else {
					// error
				}
				break;
			}

			case "produceResponse": {
				this.printable && console.debug(LOGTAG, `produceResponse`, payload);
				if (this.sendTransport && payload.transportId === this.sendTransport.id) {
					this.emit("produceResponse", payload.kind, payload.producerId);
				} else {
					// error
				}
				break;
			}

			case "produceDataResponse": {
				this.printable && console.debug(LOGTAG, `produceDataResponse`, payload);
				if (this.sendTransport && payload.transportId === this.sendTransport.id) {
					this.emit("produceDataResponse", payload.producerId);
				} else {
					// error
				}
				break;
			}

			case "notifyAllPeers": {
				if (this.status !== "joined") {
					this.printable &&
						console.debug(
							LOGTAG,
							`The current status(${this.status}) is not joined state. something wrong in notifyAllPeers!!!`
						);
					break;
				}

				let isPeerInTalkingGroup = false;
				const peerList = payload.peerList;
				peerList.forEach((peer) => {
					if (peer.endpointID !== this.userId) {
						if (this.peers.has(peer.endpointID)) {
							this.printable && console.debug(LOGTAG, `peer[${peer.endpointID} already exists.`);
							return;
						}

						this.printable && console.debug(LOGTAG, `new peer(${peer.endpointID}) has joined.`);

						const newPeer = {
							userId: peer.endpointID,
							actorId: peer.actorId,
							consumers: new Map(),
							isPinned: false
						};

						newPeer.enableAudio = true;
						if (peer.enableAudio !== undefined) {
							newPeer.enableAudio = peer.enableAudio;
						}

						newPeer.enableVideo = true;
						if (peer.enableVideo !== undefined) {
							newPeer.enableVideo = peer.enableVideo;
						}

						this.peers.set(peer.endpointID, newPeer);

						const participant = {
							actorId: peer.actorId,
							uuid: peer.endpointID
						};

						this.participants.set(peer.endpointID, participant);

						if (this.talkingGroup.includes(newPeer.actorId)) {
							isPeerInTalkingGroup = true;
						}

						if (!newPeer.enableAudio && !newPeer.enableVideo) {
							this.printable &&
								console.debug(
									LOGTAG,
									`notifyAllPeers createPeerView, userId=${newPeer.userId}, userName=${newPeer.actorId} has no audio/video.`
								);
							this.handler?.("createPeerView", this.roomId, {
								userID: newPeer.userId,
								actorID: newPeer.actorId,
								stream: null,
								soundMeter: null
							});
							this.emit("createPeerView", null, newPeer.userId, newPeer.actorId, null);
						} else {
							// 무조건 생성한 후, audio/video consumer 생성시에 stream에 track만 설정할 것.
							this.createMediaStream(newPeer, null, "none");
							this.printable &&
								console.debug(
									LOGTAG,
									`notifyAllPeers createPeerView, userId=${newPeer.userId}, userName=${newPeer.actorId}`
								);
							this.handler?.("createPeerView", this.roomId, {
								userID: newPeer.userId,
								actorID: newPeer.actorId,
								stream: newPeer.stream,
								soundMeter: newPeer.soundMeter
							});
							this.emit(
								"createPeerView",
								newPeer.stream,
								newPeer.userId,
								newPeer.actorId,
								newPeer.soundMeter
							);
						}
					}
				});

				if (isPeerInTalkingGroup) {
					this.talkingGroupWithUserId = this.createTalkingGroupWithUserId(this.talkingGroup);
					this.printable && console.debug(LOGTAG, `converted talkingGroup`, this.talkingGroupWithUserId);
					this.sendChangeTalkingGroup(this.talkingGroupWithUserId);
				}

				// this.sendChangeTalkingGroup();
				break;
			}

			case "notifyNewPeer": {
				const peer = payload;
				if (peer.endpointID === this.userId) {
					return;
				}

				if (this.status !== "joined") {
					this.printable &&
						console.debug(
							LOGTAG,
							`The current status(${this.status}) is not joined state. peer(${peer.endpointID})`
						);
					break;
				}

				if (this.peers.has(peer.endpointID)) {
					this.printable && console.debug(LOGTAG, `peer(${peer.endpointID}) already exists.`);
					return;
				}

				this.printable && console.debug(LOGTAG, `new peer(${peer.endpointID}) has joined.`);

				const newPeer = {
					userId: peer.endpointID,
					actorId: peer.actorId,
					consumers: new Map(),
					isPinned: false
				};

				newPeer.enableAudio = true;
				if (peer.enableAudio !== undefined) {
					newPeer.enableAudio = peer.enableAudio;
				}

				newPeer.enableVideo = true;
				if (peer.enableVideo !== undefined) {
					newPeer.enableVideo = peer.enableVideo;
				}

				this.peers.set(peer.endpointID, newPeer);

				const participant = {
					actorId: peer.actorId,
					uuid: peer.endpointID
				};

				this.participants.set(peer.endpointID, participant);

				if (!newPeer.enableAudio && !newPeer.enableVideo) {
					this.printable &&
						console.debug(
							LOGTAG,
							`createPeerView, notifyNewPeer userId=${newPeer.userId}, userName=${newPeer.actorId} has no audio/video.`
						);
					this.handler?.("createPeerView", this.roomId, {
						userID: newPeer.userId,
						actorID: newPeer.actorId,
						stream: null,
						soundMeter: null
					});
					this.emit("createPeerView", null, newPeer.userId, newPeer.actorId, null);
				} else {
					// 무조건 생성한 후, audio/video consumer 생성시에 stream에 track만 설정할 것.
					this.createMediaStream(newPeer, null, "none");
					this.printable &&
						console.debug(
							LOGTAG,
							`createPeerView, notifyNewPeer userId=${newPeer.userId}, userName=${newPeer.actorId}`,
							newPeer.stream,
							newPeer.stream.getTracks()
						);
					this.handler?.("createPeerView", this.roomId, {
						userID: newPeer.userId,
						actorID: newPeer.actorId,
						stream: newPeer.stream,
						soundMeter: newPeer.soundMeter
					});
					this.emit("createPeerView", newPeer.stream, newPeer.userId, newPeer.actorId, newPeer.soundMeter);
				}

				if (this.screenAudioProducer || this.screenVideoProducer) {
					this.sendScreenSharing("started", peer.endpointID);
				}

				// 새로운 참여자가 이미 talkingGroup에 있다면 changeTalkingGroup을 전송
				if (this.talkingGroup.includes(peer.actorId)) {
					this.talkingGroupWithUserId = this.createTalkingGroupWithUserId(this.talkingGroup);
					this.printable && console.debug(LOGTAG, `converted talkingGroup`, this.talkingGroupWithUserId);
					this.sendChangeTalkingGroup(this.talkingGroupWithUserId);
				}

				// this.sendChangeTalkingGroup();
				break;
			}

			case "notifyPeerLeft": {
				const peerEndpointID = payload.endpointID;
				if (peerEndpointID === this.userId) {
					return;
				}

				const peer = this.peers.get(peerEndpointID);
				if (!peer) {
					this.printable && console.debug(LOGTAG, `peer(${peerEndpointID}) is not found.`);
					return;
				}

				for (const consumer of peer.consumers.values()) {
					if (consumer.appData.consumerType === "screen") {
						if (this.removeScreenStream(peer, consumer.kind)) {
							const screenUserId = "screen_" + peerEndpointID;
							this.handler?.("removePeerScreen", this.roomId, { userID: screenUserId });
							this.emit("removePeerScreen", screenUserId);
							this.printable && console.debug(LOGTAG, `removePeerScreen: ${screenUserId}`);
						}
					} else if (consumer.appData.consumerType === "camMic") {
						this.removeMediaStream(peer, consumer.kind);
						// if (this.removeMediaStream(peer, consumer.kind)) {
						// 	this.emit("removePeerView", peerEndpointID);
						// 	this.printable && console.debug(LOGTAG, `removePeerView`, peerEndpointID);
						// }
					}

					this.removeConsumer(consumer);
					peer.consumers.delete(consumer.id);
				}

				// if (peer.soundMeter)   {
				//     peer.soundMeter.stop();
				// }

				this.printable && console.debug(LOGTAG, `The peer(${peerEndpointID}) has left.`);

				peer.stream = null;
				this.handler?.("removePeerView", this.roomId, { userID: peerEndpointID });
				this.emit("removePeerView", peerEndpointID);
				this.printable && console.debug(LOGTAG, `removePeerView`, peerEndpointID);

				this.peers.delete(peerEndpointID);

				this.participants.delete(peerEndpointID);

				// this.emit('removePeerView', peerEndpointID);

				if (this.talkingGroupWithUserId.includes(peerEndpointID)) {
					// this.talkingGroup에서는 제거할 필요가 없음. 필요시 this.changeTalkingGroup()을 unity에서 호출해야 됨.
					// peer가 peers에서 이미 제거되었기 때문에 createTalkingGroupWithUserId는 제거된 talkingGroupWithUserId를 생성.
					this.talkingGroupWithUserId = this.createTalkingGroupWithUserId(this.talkingGroup);
					this.printable && console.debug(LOGTAG, `converted talkingGroup`, this.talkingGroupWithUserId);
					this.sendChangeTalkingGroup(this.talkingGroupWithUserId); // 서버에서 해당 consumer를 제거하기 위해서 전송.
				}

				// this.sendChangeTalkingGroup();
				break;
			}

			case "newConsumer": {
				this.printable && console.debug(LOGTAG, `newConsumer`, payload);
				const peerEndpointID = payload.peerEndpointID;
				if (peerEndpointID === this.userId) {
					this.printable &&
						console.debug(LOGTAG, `error. peerEndpointID=${peerEndpointID}, endpointID=${this.userId}`);
					return;
				}

				const peer = this.peers.get(peerEndpointID);
				if (!peer) {
					this.printable && console.debug(LOGTAG, `peer(${peerEndpointID}) is not found.`);
					return;
				}

				this.addNewConsumer(peer, payload);
				break;
			}

			case "closeConsumer": {
				this.printable && console.debug(LOGTAG, `closeConsumer`, payload);
				const peerEndpointID = payload.peerEndpointID;
				if (peerEndpointID === this.userId) {
					this.printable &&
						console.debug(LOGTAG, `error. peerEndpointID=${peerEndpointID}, endpointID=${this.userId}`);
					return;
				}

				const peer = this.peers.get(peerEndpointID);
				if (!peer) {
					this.printable && console.debug(LOGTAG, `peer(${peerEndpointID}) is not found.`);
					return;
				}

				for (const consumer of peer.consumers.values()) {
					if (consumer.id === payload.id) {
						if (consumer.appData.consumerType === "screen") {
							if (this.removeScreenStream(peer, consumer.kind)) {
								const screenUserId = "screen_" + peerEndpointID;
								this.handler?.("removePeerScreen", this.roomId, { userID: screenUserId, stream: null });
								this.emit("removePeerScreen", screenUserId);
								this.printable && console.debug(LOGTAG, `removePeerScreen: ${screenUserId}`);
							}
						} else if (consumer.appData.consumerType === "camMic") {
							this.removeMediaStream(peer, consumer.kind);
							// if (this.removeMediaStream(peer, consumer.kind)) {
							// 	this.emit("removePeerView", peerEndpointID);
							// 	this.printable && console.debug(LOGTAG, `removePeerView`, peerEndpointID);
							// }
						}

						this.removeConsumer(consumer);
						peer.consumers.delete(consumer.id);
					}
				}
				break;
			}

			case "screenSharing": {
				const screenSenderId = payload.endpointID;
				if (screenSenderId === this.userId) {
					return;
				}

				this.printable && console.debug(LOGTAG, `screenSharing`, payload);

				const status = payload.status;
				if (status === "started") {
					this.sendGetScreenSharing(screenSenderId);
				} else if (status === "stopped") {
					const peer = this.peers.get(screenSenderId);
					if (!peer) {
						this.printable && console.debug(LOGTAG, `peer(${screenSenderId}) is not found.`);
						return;
					}

					for (const consumer of peer.consumers.values()) {
						if (consumer.appData.consumerType === "screen") {
							const consumerId = consumer.id;
							// removeConsumer(consumer);
							// peer.consumers.delete(consumerId);

							this.sendRemoveConsumer(consumerId);
						}
					}
				}
				break;
			}

			case "dominantSpeaker": {
				this.printable && console.debug(LOGTAG, `dominantSpeaker`, payload);
				// const dominantSpeakerID = payload.endpointID;
				break;
			}

			case "error": {
				this.printable &&
					console.debug(LOGTAG, `error: errorCode = %d, reason = %s`, payload.errorCode, payload.reason);
				break;
			}

			default: {
				this.printable && console.debug(LOGTAG, `unknown message type`, msgType);
				break;
			}
		}
	}

	checkAndSendGetConsumer(newId) {
		if (newId !== this.userId) {
			const peer = this.peers.get(newId);
			if (peer && peer.enableVideo) {
				this.printable && console.debug(LOGTAG, `new dominantSpeaker(${newId}, getting a video ...)`);
				this.sendGetConsumer(newId, "video");
			}
		}
	}

	checkAndSendRemoveConsumer(oldId) {
		if (oldId !== this.userId) {
			const peer = this.peers.get(oldId);
			if (peer) {
				if (!peer.isPinned) {
					this.printable &&
						console.debug(LOGTAG, `previous dominantSpeaker(${oldId}, removing this video ...)`);
					for (const consumer of peer.consumers.values()) {
						if (consumer.appData.consumerType === "camMic" && consumer.kind === "video") {
							this.removeMediaStream(peer, consumer.kind);
							// if (this.removeMediaStream(peer, consumer.kind)) {
							// 	this.emit("removePeerView", oldId);
							// 	this.printable && console.debug("removePeerView: ", oldId);
							// }
							this.removeConsumer(consumer);
							peer.consumers.delete(consumer.id);
							this.sendRemoveConsumer(consumer.id);
							break;
						}
					}
				} else {
					this.printable &&
						console.debug(LOGTAG, `checkAndSendRemoveConsumer, ${oldId} is pinned. Do not remove.`);
				}
			}
		}
	}

	createTransport(serverTransportParams) {
		const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters, isProducer } = serverTransportParams;

		this.printable && console.debug(LOGTAG, `createTransport, iceServers:`, Properties.iceServers);

		let transport;
		if (isProducer) {
			transport = this.mediasoupDevice.createSendTransport({
				id,
				iceParameters,
				iceCandidates,
				dtlsParameters,
				sctpParameters,
				// iceServers: this.config.iceServers,
				iceServers: Properties.iceServers
				// iceTransportPolicy	: 'relay',
				// // TODO: Fix for issue #72
				// iceTransportPolicy     : this._device.flag === 'firefox' && this._turnServers ? 'relay' : undefined,
				// proprietaryConstraints : PC_PROPRIETARY_CONSTRAINTS
			});
		} else {
			transport = this.mediasoupDevice.createRecvTransport({
				id,
				iceParameters,
				iceCandidates,
				dtlsParameters,
				sctpParameters,
				// iceServers: this.config.iceServers,
				iceServers: Properties.iceServers
				// iceTransportPolicy	: 'relay',
				// // TODO: Fix for issue #72
				// iceTransportPolicy : this._device.flag === 'firefox' && this._turnServers ? 'relay' : undefined,
				// additionalSettings : {
				// 	encodedInsertableStreams : insertableStreamsSupported && enableOpusDetails
				// },
				// appData : {
				// 	encodedInsertableStreams : insertableStreamsSupported && enableOpusDetails
				// }
			});
		}

		this.printable && console.debug(LOGTAG, `transport`, transport);

		transport.on("connect", async ({ dtlsParameters }, callback, errback) => {
			this.printable && console.debug(LOGTAG, `transport(isProducer: ${isProducer}), connect`, errback);
			this.sendConnectWebRtcTransport(transport.id, dtlsParameters);
			transport.once("connectWebRtcTransportResponse", () => {
				this.printable &&
					console.debug(LOGTAG, `Connections of both sides are Done. isProducer: ${isProducer}`);
				callback();
			});
		});

		transport.on("connectionstatechange", (connectState) => {
			this.printable &&
				console.debug(
					LOGTAG,
					`transport(isProducer: ${isProducer}), connectionstatechange, connectState(${connectState})`
				);
			// switch (connectState)   {
			// 	case 'disconnected':
			// 	case 'failed':
			// 		this.restartIce(this.transport, this._sendRestartIce, 2000);
			// 		break;

			// 	default:
			// 		clearTimeout(this._sendRestartIce.timer);
			// 		break;
			// }
		});

		if (isProducer) {
			transport.on("produce", async ({ kind, rtpParameters, appData }, callback, errback) => {
				this.printable && console.debug(LOGTAG, `transport, produce, kind`, kind, errback);
				await this.sendProduce(transport.id, kind, rtpParameters, appData).then((producerId) => {
					callback({ id: producerId });
				});
			});

			transport.on(
				"producedata",
				async ({ sctpStreamParameters, label, protocol, appData }, callback, errback) => {
					this.printable && console.debug(LOGTAG, `transport, producedata, label`, label, errback);
					await this.sendProduceData(transport.id, sctpStreamParameters, label, protocol, appData).then(
						(producerId) => {
							callback({ id: producerId });
						}
					);
				}
			);
		}

		return transport;
	}

	async produceStream(params) {
		// we now call produce() to instruct the producer transport
		// to send media to the Router
		// https://mediasoup.org/documentation/v3/mediasoup-client/api/#transport-produce
		// this action will trigger the 'connect' and 'produce' events above

		this.printable && console.debug(LOGTAG, `produceStream, params`, params);

		const producer = await this.sendTransport.produce(params);
		this.printable && console.debug(LOGTAG, `producer`, producer);

		producer.on("trackended", () => {
			this.printable && console.debug(LOGTAG, `track ended`, producer.id, this._stop_manual);

			this._stop_manual === false && EventHandler.fire("$Unity::Event$", "screenShareStopped");
			this._stop_manual === false && UnityApiController.instance.stopScreenSharing();

			// close a track
			this.stopScreenSharing();
		});

		producer.on("transportclose", () => {
			this.printable && console.debug(LOGTAG, `transport ended`, producer.id);

			// close a track
		});

		return producer;
	}

	async produceDataStream() {
		this.printable && console.debug(LOGTAG, `produceDataStream`);

		const dataProducer = await this.sendTransport.produceData({
			ordered: true, // reliable transmission
			label: "heartbeat",
			priority: "medium",
			// maxRetransmits : 1,
			appData: { producerType: "heartbeat-dataProducer" }
		});

		this.printable && console.debug(LOGTAG, `dataProducer`, dataProducer);

		dataProducer.on("transportclose", () => {
			this.printable && console.debug(LOGTAG, `transport ended`, dataProducer.id);
		});

		dataProducer.on("open", () => {
			this.printable && console.debug(LOGTAG, `dataProducer "open" event`, dataProducer.id);
		});

		dataProducer.on("close", () => {
			this.printable && console.debug(LOGTAG, `dataProducer "close" event`, dataProducer.id);
		});

		dataProducer.on("error", (error) => {
			this.printable && console.debug(LOGTAG, `dataProducer "error" event(${error}): ${dataProducer.id}`);
		});

		return dataProducer;
	}

	async addNewConsumer(peer, newConsumerParam) {
		const { id, kind, rtpParameters, producerId, appData } = newConsumerParam;

		const consumer = await this.recvTransport.consume({
			id,
			producerId,
			kind,
			rtpParameters,
			appData
		});

		this.printable && console.debug(LOGTAG, `consumer`, consumer, `serverside consumer id`, id);

		consumer.on("transportclose", () => {
			this.printable && console.debug(LOGTAG, `transportclose of consumer(${consumer.id})`);
			// consumers.delete(consumer.id);
		});

		peer.consumers.set(consumer.id, consumer);

		const { track } = consumer;

		if (appData.consumerType === "camMic") {
			if (this.createMediaStream(peer, track, kind)) {
				this.printable &&
					console.debug(
						LOGTAG,
						`addNewConsumer createPeerView, userId=${peer.userId}, userName=${peer.actorId}`
					);
				this.handler?.("createPeerView", this.roomId, {
					userID: peer.userId,
					actorID: peer.actorId,
					stream: peer.stream,
					soundMeter: peer.soundMeter
				});
				this.emit("createPeerView", peer.stream, peer.userId, peer.actorId, peer.soundMeter);
			}
		} else if (appData.consumerType === "screen") {
			if (this.createScreenStream(peer, track, kind)) {
				const screenUserId = "screen_" + peer.userId;
				this.printable &&
					console.debug(LOGTAG, `createPeerScreen, userId=${screenUserId}, userName=${peer.actorId}`);
				this.handler?.("createPeerScreen", this.roomId, {
					userID: screenUserId,
					actorID: peer.actorId,
					stream: peer.screenStream,
					soundMeter: null
				});
				this.emit("createPeerScreen", peer.screenStream, screenUserId, peer.actorId, null, peer.userId);
			}
		} else {
			this.printable && console.debug(LOGTAG, `unknown consumerType`, appData.consumerType);
		}

		this.sendResumeConsumer(id);
	}

	createMediaStream(peer, track, kind) {
		let isNewPeerStream = false;
		if (!peer.stream) {
			peer.stream = new MediaStream();
			peer.soundMeter = new SoundMeter("XmsRemote", new AudioContext());
			isNewPeerStream = true;
		}

		if (track) {
			peer.stream.addTrack(track);
		}

		if (kind === "audio") {
			if (peer.audioTrack) {
				// error
				this.printable &&
					console.debug(LOGTAG, `createMediaStream, peer(${peer.userId}) has audioTrack already.`);
			}
			peer.audioTrack = track;
			peer.soundMeter
				.connectToSource(peer.stream)
				.then(() => {
					this.printable && console.debug(LOGTAG, "Successfully connected to the source.");
				})
				.catch((e) => {
					this.printable && console.warn(LOGTAG, `soundMeter error`, e);
				});
		} else if (kind === "video") {
			if (peer.videoTrack) {
				// error
				this.printable &&
					console.debug(LOGTAG, `createMediaStream, peer(${peer.userId}) has videoTrack already.`);
			}
			peer.videoTrack = track;
		}

		// if (peer.audioTrack && peer.videoTrack)   {
		//     return true;
		// }
		return isNewPeerStream;
	}

	removeMediaStream(peer, kind) {
		if (!peer.stream) {
			this.printable &&
				console.debug(LOGTAG, `removeMediaStream, peer(${peer.userId}) doesnot have a mediaStream.`);
			return false;
		}

		if (kind === "audio") {
			if (peer.audioTrack) {
				if (peer.soundMeter) {
					peer.soundMeter.stop();
				}
				peer.stream.removeTrack(peer.audioTrack);
				peer.audioTrack = null;
			} else {
				this.printable &&
					console.debug(LOGTAG, `removeMediaStream, peer(${peer.userId}) does not have a audioTrack.`);
			}
		} else if (kind === "video") {
			if (peer.videoTrack) {
				peer.stream.removeTrack(peer.videoTrack);
				peer.videoTrack = null;
			} else {
				this.printable &&
					console.debug(LOGTAG, `removeMediaStream, peer(${peer.userId}) does not have a videoTrack.`);
			}
		}

		// if (!peer.audioTrack && !peer.videoTrack) {
		// 	peer.stream = null;
		// 	return true;
		// }

		return false;
	}

	createScreenStream(peer, track, kind) {
		let isNewPeerStream = false;
		if (!peer.screenStream) {
			peer.screenStream = new MediaStream();
			isNewPeerStream = true;
		}

		peer.screenStream.addTrack(track);

		if (kind === "audio") {
			if (peer.screenAudioTrack) {
				// error
				this.printable &&
					console.debug(LOGTAG, `createScreenStream, peer(${peer.userId}) has audioTrack already.`);
			}
			peer.screenAudioTrack = track;
			this.printable &&
				console.debug(
					LOGTAG,
					`createScreenStream, the peer(${peer.userId}) creates a audio stream for screen sharing.`
				);
		} else if (kind === "video") {
			if (peer.screenVideoTrack) {
				// error
				this.printable &&
					console.debug(LOGTAG, `createScreenStream, peer(${peer.userId}) has videoTrack already.`);
			}
			peer.screenVideoTrack = track;
			this.printable &&
				console.debug(
					LOGTAG,
					`createScreenStream, the peer(${peer.userId}) creates a video stream for screen sharing.`
				);
		}

		return isNewPeerStream;
	}

	removeScreenStream(peer, kind) {
		if (!peer.screenStream) {
			this.printable &&
				console.debug(LOGTAG, `removeScreenStream, peer(${peer.userId}) doesnot have a mediaStream.`);
			return false;
		}

		if (kind === "audio") {
			if (peer.screenAudioTrack) {
				peer.screenStream.removeTrack(peer.screenAudioTrack);
				peer.screenAudioTrack = null;
			} else {
				this.printable &&
					console.debug(LOGTAG, `removeScreenStream, peer(${peer.userId}) does not have a audioTrack.`);
			}
		} else if (kind === "video") {
			if (peer.screenVideoTrack) {
				peer.screenStream.removeTrack(peer.screenVideoTrack);
				peer.screenVideoTrack = null;
			} else {
				this.printable &&
					console.debug(LOGTAG, `removeScreenStream, peer(${peer.userId}) does not have a videoTrack.`);
			}
		}

		if (!peer.screenAudioTrack && !peer.screenVideoTrack) {
			peer.screenStream = null;
			return true;
		}

		return false;
	}

	removeConsumer(consumer) {
		// removePeerView(consumer);
		// this.emit('removePeerView', consumer.producerId);
		consumer.close();
	}

	// localStreamCallback의 위치는? constructor?
	async startScreenSharing(localStreamCallback) {
		this._stop_manual = false;

		const { width, height } = XmsUtils.getVideoResolution(this.configure.screenResolution);
		this.printable && console.debug(LOGTAG, `startScreenSharing, screen max width = ${width}, height = ${height}`);

		let stream = null;
		try {
			// const stream = await navigator.mediaDevices.getDisplayMedia({audio: true});
			stream = await navigator.mediaDevices.getDisplayMedia({
				audio: true,
				video: {
					width: { max: width },
					height: { max: height }
				}
			});
		} catch (error) {
			this.printable && console.debug(LOGTAG, `cancel screen sharing with`, error);
			EventHandler.fire("$Unity::Event$", "cancelScreenShare");
			UnityApiController.instance.cancelScreenSharing();
			return -2;
		}

		const audioTrack = stream.getAudioTracks()[0];
		const videoTrack = stream.getVideoTracks()[0];

		if (this.screenStream) {
			await this.stopScreenSharing();
		}
		if (audioTrack) {
			const screenAudioParams = { track: audioTrack, appData: { producerType: "screen" } };
			this.printable && console.debug(LOGTAG, `screen audio params`, screenAudioParams);

			this.screenAudioProducer = await this.produceStream(screenAudioParams);
		}

		if (videoTrack) {
			const screenVideoParams = { track: videoTrack, appData: { producerType: "screen" } };
			this.printable && console.debug(LOGTAG, `screen video params`, screenVideoParams);

			this.screenVideoProducer = await this.produceStream(screenVideoParams);
		}

		this.screenStream = stream;

		const screenUserId = "screen_" + this.userId;
		this.handler?.("createPeerScreen", this.roomId, {
			userID: this.userId,
			actorID: this.actorId,
			stream: stream
		});
		this.emit("createPeerScreen", stream, screenUserId, this.actorId, null);
		if (audioTrack) {
			if (videoTrack) {
				const localStream = new MediaStream();
				localStream.addTrack(videoTrack);
				localStreamCallback && localStreamCallback(localStream, screenUserId);
			} else {
				localStreamCallback && localStreamCallback(null, screenUserId);
			}
		} else {
			localStreamCallback && localStreamCallback(stream, screenUserId);
		}
		this.sendScreenSharing("started");

		return 0;
	}

	stopScreenSharing() {
		this._stop_manual = true;
		this.printable && console.debug(LOGTAG, `stopScreenSharing`);
		if (!this.screenAudioProducer && !this.screenVideoProducer) {
			this.printable && console.debug(LOGTAG, `not screenSharing mode.`);
			return;
		}

		// remove producers
		if (this.screenAudioProducer) {
			this.sendRemoveProducer(this.screenAudioProducer.id);

			this.screenAudioProducer.close();
			this.screenAudioProducer = null;
		}

		if (this.screenVideoProducer) {
			this.sendRemoveProducer(this.screenVideoProducer.id);

			this.screenVideoProducer.close();
			this.screenVideoProducer = null;
		}

		this.sendScreenSharing("stopped");

		// remove tracks
		const stream = this.screenStream;
		if (!(stream instanceof MediaStream)) {
			return;
		}

		stream.getTracks().forEach((track) => track.stop());
		this.screenStream = null;
	}

	// functions for sending MQTT
	async sendJoin() {
		return new Promise((resolve, reject) => {
			this.xmsNetwork.sendToXms("join", {
				// join:   {
				conferenceID: this.roomId,
				endpointID: this.userId,
				actorId: this.actorId,
				enableDominantSpeaker: this.enableDominantSpeaker,
				enableLoudestSpeakers: this.enableLoudestSpeakers
				// }
			});

			this.once("joinResponse", (result, numOfLoudestSpeakers) => {
				this.printable &&
					console.debug(
						LOGTAG,
						`joinResponse received. result: ${result}, numOfLoudestSpeakers: ${numOfLoudestSpeakers}`
					);
				if (result === "success") {
					if (numOfLoudestSpeakers) {
						this.numOfLoudestSpeakers = numOfLoudestSpeakers;
					}
					resolve();
				} else {
					this.printable && console.debug(LOGTAG, `joinResponse error.`);
					reject();
				}
			});
			// error 발생시에 reject() 호출
			// timer 같은 것을 사용하여 reject() 호출.
		});
	}

	sendJoinComplete() {
		const enableAudio = !!this.audioProducer;
		const enableVideo = !!this.videoProducer;

		this.xmsNetwork.sendToXms("joinComplete", {
			// joinComplete:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			enableAudio,
			enableVideo
			// }
		});
	}

	async sendGetServerCapabilities() {
		return new Promise((resolve) => {
			this.xmsNetwork.sendToXms("getServerCapabilities", {
				// getServerCapabilities:   {
				conferenceID: this.roomId,
				endpointID: this.userId
				// }
			});

			this.once("getServerCapabilitiesResponse", (rtpCapabilities) => {
				this.printable && console.debug(LOGTAG, `getServerCapabilitiesResponse received.`, rtpCapabilities);
				resolve(rtpCapabilities);
			});
			// error 발생시에 reject() 호출
			// timer 같은 것을 사용하여 reject() 호출.
		});
	}

	async sendCreateWebRtcTransport({ isProducer }) {
		return new Promise((resolve) => {
			this.printable && console.debug(LOGTAG, `rtpCapabilities`, this.mediasoupDevice.rtpCapabilities);

			this.xmsNetwork.sendToXms("createWebRtcTransport", {
				// createWebRtcTransport:   {
				conferenceID: this.roomId,
				endpointID: this.userId,
				isProducer,
				rtpCapabilities: this.mediasoupDevice.rtpCapabilities,
				sctpCapabilities: this.mediasoupDevice.sctpCapabilities
				// }
			});

			this.once("createWebRtcTransportResponse", (transportParams) => {
				this.printable && console.debug(LOGTAG, `createWebRtcTransportResponse received.`, transportParams);
				resolve(transportParams);
			});
			// error 발생시에 reject() 호출
			// timer 같은 것을 사용하여 reject() 호출.
		});
	}

	sendConnectWebRtcTransport(transportId, dtlsParameters) {
		this.xmsNetwork.sendToXms("connectWebRtcTransport", {
			// connectWebRtcTransport:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			transportId,
			dtlsParameters
			// }
		});
	}

	async sendProduce(transportId, kind, rtpParameters, appData) {
		return new Promise((resolve) => {
			this.xmsNetwork.sendToXms("produce", {
				// produce:   {
				conferenceID: this.roomId,
				endpointID: this.userId,
				transportId,
				kind,
				rtpParameters,
				appData
				// }
			});

			this.once("produceResponse", (producerKind, producerId) => {
				this.printable &&
					console.debug(LOGTAG, `produceResponse received. producerKind=${producerKind}, ${producerId}`);
				resolve(producerId);
			});
			// error 발생시에 reject() 호출
			// timer 같은 것을 사용하여 reject() 호출.
		});
	}

	async sendProduceData(transportId, sctpStreamParameters, label, protocol, appData) {
		return new Promise((resolve) => {
			this.xmsNetwork.sendToXms("produceData", {
				// produceData:   {
				conferenceID: this.roomId,
				endpointID: this.userId,
				transportId,
				sctpStreamParameters,
				label,
				protocol,
				appData
				// }
			});

			this.once("produceDataResponse", (producerId) => {
				this.printable && console.debug(LOGTAG, `produceDataResponse received. ${producerId}`);
				resolve(producerId);
			});
			// error 발생시에 reject() 호출
			// timer 같은 것을 사용하여 reject() 호출.
		});
	}

	sendChangeTalkingGroup(talkingGroup) {
		// loudestSpeakers 모드에서는 audio consumer들을 초기에 생성후, producer들간에 공유함.
		// loudestSpeakers 모드에서는 changeTalkingGroup을 보내서 audio consumer들을 변경하지 않음.
		if (this.enableLoudestSpeakers) {
			return;
		}

		let allTalkingGroup;
		if (!talkingGroup) {
			allTalkingGroup = Array.from(this.peers.keys());
			this.printable && console.debug(LOGTAG, `allTalkingGroup`, allTalkingGroup);
		} else {
			this.printable && console.debug(LOGTAG, `talkingGroup`, talkingGroup);
		}

		this.xmsNetwork.sendToXms("changeTalkingGroup", {
			// changeTalkingGroup: {
			conferenceID: this.roomId,
			endpointID: this.userId,
			talkingGroup: talkingGroup ? talkingGroup : allTalkingGroup,
			kind: this.enableDominantSpeaker ? "audio" : "all"
			// }
		});
	}

	sendResumeConsumer(serverConsumerId) {
		this.xmsNetwork.sendToXms("resumeConsumer", {
			// resumeConsumer: {
			conferenceID: this.roomId,
			endpointID: this.userId,
			serverConsumerId
			// }
		});
	}

	sendLeave() {
		this.xmsNetwork.sendToXms("leave", {
			// leave:   {
			conferenceID: this.roomId,
			endpointID: this.userId
			// }
		});
	}

	sendPauseProducer(producerId) {
		this.xmsNetwork.sendToXms("pauseProducer", {
			// pauseProducer:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			producerId
			// }
		});
	}

	sendResumeProducer(producerId) {
		this.xmsNetwork.sendToXms("resumeProducer", {
			// resumeProducer:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			producerId
			// }
		});
	}

	sendRemoveProducer(producerId) {
		this.xmsNetwork.sendToXms("removeProducer", {
			// removeProducer:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			producerId
			// }
		});
	}

	sendScreenSharing(status, peerId) {
		const message = {
			// screenSharing:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			status
			// }
		};

		if (peerId) {
			this.xmsNetwork.sendToUser("screenSharing", message, peerId);
		} else {
			this.xmsNetwork.sendToRoom("screenSharing", message);
		}
	}

	sendGetScreenSharing(screenSenderId) {
		this.xmsNetwork.sendToXms("getScreenSharing", {
			// getScreenSharing:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			screenSenderID: screenSenderId
			// }
		});
	}

	sendGetConsumer(targetEndpointID, kind) {
		this.xmsNetwork.sendToXms("getConsumer", {
			// getConsumer:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			targetEndpointID,
			...((kind && { kind }) || {})
			// }
		});
	}

	sendRemoveConsumer(consumerId) {
		this.xmsNetwork.sendToXms("removeConsumer", {
			// removeConsumer:   {
			conferenceID: this.roomId,
			endpointID: this.userId,
			consumerId
			// }
		});
	}

	getAudioVolumeLevel() {
		return this.soundMeter.instant;
	}

	// TODO: audio/video가 pause되었을때, sendPauseProducer를 보내면 서버에서 producer를 pause함. 이 경우
	// 데이터를 consumer로 전송하지 않음. => 확인사항) SDP renegotiation이 발생함? A/V sync 때문에 영상/소리 안 들리는 경우는?
	mute(isMute) {
		if (!this.audioProducer) {
			this.printable && console.debug(LOGTAG, `no audio device`);
		}

		// volume이 0인 스트림 전송.
		if (isMute) {
			if (this.audioProducer) {
				this.audioProducer.pause();
				this.sendPauseProducer(this.audioProducer.id);
				this.printable && console.debug(LOGTAG, `audio is paused.`);
			}
		} else {
			if (this.audioProducer) {
				this.audioProducer.resume();
				this.sendResumeProducer(this.audioProducer.id);
				this.printable && console.debug(LOGTAG, `audio is resumed.`);
			}
		}

		// 위의 pause()/resume()과 동일한 것 같음.
		// if (this.audioVideoStream)   {
		//     this.printable && console.debug(LOGTAG, 'mute: ', isMute);
		//     this.audioVideoStream.getAudioTracks().forEach(track => {
		//         track.enabled = !isMute;
		//     });
		// }
	}

	getMuteState() {
		if (!this.audioProducer) {
			// this.printable && console.debug(LOGTAG, 'getMuteState, no audioProducer');
			return true;
		}

		// this.printable && console.debug(LOGTAG, 'getMuteState, mute=', this.audioProducer.paused);
		return this.audioProducer.paused;
	}

	enableWebcam(enable) {
		// TODO: pause시키는 것이 맞나? 완전히 disable시키는 것이 맞음?
		// 다른 프로그램들은 disable시킴. 즉 producer를 없애고 enable되었을때 다시 생성.
		// disable 할려면 this.videoProducer.close()를 하고 enable시에 producer를 다시 생성해야 됨.

		(async () => {
			if (!this.videoProducer) {
				this.printable && console.debug(LOGTAG, `no video device`);
			}

			const { width, height } = XmsUtils.getVideoResolution(this.configure.resolution);

			// pause/resume => black화면 전송.
			if (enable) {
				if (this.videoProducer) {
					let video_stream = null;
					// eslint-disable-next-line no-useless-catch
					try {
						EventHandler.fire("$Device::Permission$");
						const constraints = DeviceManager.instance.get_media_constraints({
							video: {
								width: {
									min: 320,
									max: width
								},
								height: {
									min: 180,
									max: height
								}
							}
						});
						video_stream = await navigator.mediaDevices.getUserMedia(constraints);
					} catch (error) {
						console.warn("#xms_client# getUserMedia error: ", error);
						return;
					}

					this.videoProducer?.pause?.();
					try {
						await this.videoProducer.replaceTrack({ track: video_stream.getVideoTracks()[0] });
					} catch (error) {
						this.printable && console.debug(LOGTAG, `replaceTrack error. ${error}`);
					}
					this.audioVideoStream.removeTrack(this.audioVideoStream.getVideoTracks()[0]);
					this.audioVideoStream.addTrack(video_stream.getVideoTracks()[0]);
					this.videoProducer.resume();

					this.sendResumeProducer(this.videoProducer.id);
					this.printable && console.debug(LOGTAG, `video is resumed.`);
				}
			} else {
				if (this.videoProducer) {
					this.videoProducer?.pause?.();
					const track = this._dummyStream.create(width, height).getVideoTracks()[0];
					try {
						await this.videoProducer.replaceTrack({ track });
					} catch (error) {
						this.printable && console.debug(LOGTAG, `replaceTrack error. ${error}`);
					}
					this.audioVideoStream.removeTrack(this.audioVideoStream.getVideoTracks()[0]);
					this.audioVideoStream.addTrack(track);
					this.videoProducer.resume();

					this.videoProducer?.pause?.();
					this.sendPauseProducer(this.videoProducer.id);
					this.printable && console.debug(LOGTAG, `video is paused.`);
				}
			}
		})();
		// 위의 pause()/resume()과 동일한 것 같음.
		// if (this.audioVideoStream)   {
		//     this.printable && console.debug(LOGTAG, 'enableWebcam: ', enable);
		//     this.audioVideoStream.getVideoTracks().forEach(track => {
		//         track.enabled = enable;
		//     });
		// }

		// if (this.audioVideoStream)   {
		//     this.printable && console.debug(LOGTAG, 'enableWebcam: ', enable);
		//     if (!enable)   {
		//         this.audioVideoStream.getVideoTracks().forEach(track => {
		//             track.stop();
		//         });
		//     }
		//     else   {
		//         // getUserMedia()
		//     }
		// }
	}

	setUserPin(peerId, isPinned) {
		this.printable && console.debug(LOGTAG, `setUserPin, peerId = ${peerId}, isPinned = ${isPinned}`);
		const peer = this.peers.get(peerId);
		if (!peer) {
			this.printable && console.debug(LOGTAG, `setUserPin, peer(${peerId}) is not found.`);
			return;
		}

		peer.isPinned = isPinned;

		if (isPinned) {
			if (peer.enableVideo) {
				// dominantSpeakers에 포함되어서 이미 생성된 경우에는 xms 서버에서 무시함.
				this.sendGetConsumer(peerId, "video");
			}
		} else {
			if (this.dominantSpeakers.includes(peerId)) {
				this.printable &&
					console.debug(
						LOGTAG,
						`unPinUser, peer(${peerId}) is included in dominantSpeakers(${this.dominantSpeakers})`
					);
				return;
			}

			for (const consumer of peer.consumers.values()) {
				if (consumer.appData.consumerType === "camMic" && consumer.kind === "video") {
					this.removeMediaStream(peer, consumer.kind);
					// if (this.removeMediaStream(peer, consumer.kind)) {
					// 	this.emit("removePeerView", peerId);
					// 	this.printable && console.debug("removePeerView: ", peerId);
					// }

					const consumerId = consumer.id;
					this.removeConsumer(consumer);
					peer.consumers.delete(consumerId);
					this.sendRemoveConsumer(consumerId);
				}
			}
		}
	}

	/**
	 * @param {Object} param0
	 * @param {string} param0.seqNum
	 * @param {string} param0.recordingFilename
	 */
	startRecording({ seqNum, recordingFilename }) {
		if (!seqNum) {
			throw Error("seqNum is required");
		}
		this.xmsNetwork.sendToXms("startRecording", {
			conferenceID: this.roomId,
			endpointID: this.userId,
			authToken: Configure.authorizationKey || "",
			kind: "audio",
			seqNum,
			recordingFilename
		});
	}

	/**
	 * @param {Object} param0
	 * @param {string} param0.seqNum
	 */
	stopRecording({ seqNum }) {
		if (!seqNum) {
			throw Error("seqNum is required");
		}
		this.xmsNetwork.sendToXms("stopRecording", {
			conferenceID: this.roomId,
			endpointID: this.userId,
			authToken: Configure.authorizationKey || "",
			seqNum
		});
	}

	setPhoneMode(mode) {
		// "phone" | "call" | "none
		this.printable && console.debug(LOGTAG, "setPhoneMode", mode);
	}
}
